import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { catchError, throwError } from 'rxjs';
import { SipConfig, SipConfigUpdate } from '../../../shared/models/sip.model';
import { NotificationService } from '../../../shared/services/notification.service';
import { SipService } from '../../../shared/services/sip.service';
import { ValidationService, Validators as Vld } from '../../../shared/services/validation.service';

@Component({
  standalone: false,
  selector: 'app-sip-config-form',
  templateUrl: './sip-config-form.component.html',
  styleUrls: ['./sip-config-form.component.scss'],
})
export class SipConfigFormComponent implements OnInit {
  @Input() model: SipConfigUpdate;

  @Output() onAfterSave = new EventEmitter<SipConfig>();
  @Output() onCancel = new EventEmitter<void>();

  loading = false;

  form: FormGroup;

  hasUsernameInitial = false;
  passwordRequired = false;
  usernameRequired = false;
  static readonly USERNAME_VLD = {
    regexp: /^[A-Za-z0-9._-]+$/,
    message: 'Only Latin letters (A–Z, a–z), digits (0–9), dots (.), underscores (_) and dashes (‐) are allowed.',
  };
  static readonly PASSWORD_VLD = {
    regexp: /^[^\s"\\]+$/u,
    message: 'Only letters, digits, special symbols except spaces, " and \\',
  };

  constructor(
    private readonly notificationService: NotificationService,
    private readonly formBuilder: FormBuilder,
    public service: SipService,
    public validationService: ValidationService
  ) {}

  ngOnInit() {
    this.hasUsernameInitial = !!this.model.username;
    const usernameVld = Vld.regexp(SipConfigFormComponent.USERNAME_VLD.regexp, SipConfigFormComponent.USERNAME_VLD.message, true);
    const passwordVld = Vld.regexp(SipConfigFormComponent.PASSWORD_VLD.regexp, SipConfigFormComponent.PASSWORD_VLD.message, true);
    this.form = this.formBuilder.group(
      {
        id: [this.model.id],
        name: [this.model.name, Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(255)])],
        host: [this.model.host, Vld.compose([Vld.required, Vld.domainOrIp(true), Vld.maxLength(255)])],
        port: [this.model.port, Vld.compose([Vld.required, Vld.positiveInteger(true)])],
        protocol: [{ value: this.model.protocol, disabled: true }, Vld.compose([Vld.required])], // enable after TCP support in backend ...
        prefix: [this.model.prefix, Vld.compose([Vld.maxLength(20), Vld.sipPrefix(true)])],
        cps: [this.model.cps, Vld.compose([Vld.integer(true), Vld.min(1), Vld.max(20)])],
        enabled: [this.model.enabled],
        pingEnabled: [this.model.pingEnabled],
        username: [this.model.username],
        password: [this.model.password],
      },
      {
        validators: (form: FormGroup): ValidationErrors | null => {
          const usernameControl = form.get('username');
          const passwordControl = form.get('password');
          const hasUsername = !!usernameControl.value;
          const hasPassword = !!passwordControl.value;
          const hasCredentials = hasUsername || hasPassword;
          const hasId = !!this.model.id;
          this.usernameRequired = hasCredentials;
          this.passwordRequired = hasId ? hasUsername && !this.hasUsernameInitial : hasUsername;
          usernameControl.setValidators(
            Vld.compose([this.usernameRequired ? Vld.required : null, Vld.minLength(3), Vld.maxLength(32), usernameVld].filter(_ => _ !== null))
          );
          passwordControl.setValidators(
            Vld.compose([this.passwordRequired ? Vld.required : null, Vld.minLength(12), Vld.maxLength(128), passwordVld].filter(_ => _ !== null))
          );
          usernameControl.updateValueAndValidity({ onlySelf: true });
          passwordControl.updateValueAndValidity({ onlySelf: true });
          return null;
        },
      }
    );
  }

  onSubmit() {
    this.loading = true;
    const data = this.form.getRawValue() as SipConfigUpdate;
    if (!data.id && (!data.username || !data.password)) {
      data.username = null;
      data.password = null;
    }
    if (!data.username) {
      data.username = null;
    }
    if (!data.password) {
      data.password = null;
    }
    data.name = data.name.trim();
    this.service
      .saveConfig(data)
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'SIP config',
            message: 'An error occurred while creating/updating the config',
            serviceName: 'SIP',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ?? null,
          });
          return throwError(() => e);
        })
      )
      .subscribe(d => {
        this.loading = false;
        this.onAfterSave.emit(d);
        this.notificationService.success('Config ' + (data.id ? 'updated' : 'created'), 'SIP config');
      });
  }
}
